import { useMutation, useQuery } from '@tanstack/react-query';
import { optimizationApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { debug } from '@/utils/debug';
import { features } from '@/config/features';
import type { AppConfiguration, ColdStartOptimizationRequest } from '@/types/api';

// Note: This hook is kept for backward compatibility but not currently used
// Use useColdStartMutation for cold start optimization instead
export function useOptimizationMutation() {
  const { showNotification } = useUIStore();

  return useMutation({
    mutationFn: () => {
      // This would need to be updated to use the correct API endpoint
      throw new Error('useOptimizationMutation is deprecated. Use useColdStartMutation instead.');
    },
    onSuccess: (result) => {
      debug.log('Optimization completed:', result);
      showNotification({
        type: 'success',
        message: 'Optimization completed',
      });
    },
    onError: (error: unknown) => {
      debug.error('Optimization failed:', error);
      const apiError = error as { response?: { data?: { detail?: string } } };
      showNotification({
        type: 'error',
        message: apiError.response?.data?.detail || 'Optimization failed. Please try again.',
      });
    },
  });
}

export function useAppConfigQuery() {
  const { memberUuid, isAuthenticated } = useAuthStore();
  const freshness = features.campaignDataFreshness;

  // Adjust stale time based on freshness mode
  // For refresh_on_login: Cache forever (matches campaigns query behavior)
  // This allows the Account page to reuse in-progress prefetch if user navigates early
  const staleTime = freshness === 'refresh_on_login'
    ? Infinity // Cache forever (until logout)
    : 5 * 60 * 1000; // 5 minutes for other modes

  return useQuery({
    queryKey: ['app-config', memberUuid],
    queryFn: async () => {
      if (!memberUuid) throw new Error('Member ID is required');
      debug.log(`useAppConfigQuery: Fetching config for member ${memberUuid} (mode: ${freshness})`);
      try {
        return await optimizationApi.loadConfiguration(memberUuid);
      } catch (error: unknown) {
        // If 404, return null (config doesn't exist yet for new users)
        const apiError = error as { response?: { status?: number } };
        if (apiError.response?.status === 404) {
          debug.log(`useAppConfigQuery: No saved config found for member ${memberUuid} (new user)`);
          return null;
        }
        // Re-throw other errors
        throw error;
      }
    },
    enabled: isAuthenticated && !!memberUuid,
    staleTime: staleTime,
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: unknown) => {
      // Don't retry 404 errors (config doesn't exist)
      const apiError = error as { response?: { status?: number } };
      if (apiError?.response?.status === 404) return false;
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
    // For refresh_on_login: use cached config (don't refetch on mount)
    // For other modes: always refetch to get latest configuration
    refetchOnMount: freshness === 'refresh_on_login' ? false : 'always',
  });
}

export function useFreshAppConfigQuery() {
  const { memberUuid, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['app-config-fresh', memberUuid],
    queryFn: () => {
      if (!memberUuid) throw new Error('Member ID is required');
      return optimizationApi.loadFreshConfiguration(memberUuid);
    },
    enabled: isAuthenticated && !!memberUuid,
    staleTime: 2 * 60 * 1000, // 2 minutes (fresher data)
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

export function useGoogleAdsConfigQuery(daysBack: number = 30) {
  const { memberUuid, isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: ['app-config-google-ads', memberUuid, daysBack],
    queryFn: () => {
      if (!memberUuid) throw new Error('Member ID is required');
      return optimizationApi.loadGoogleAdsConfiguration(memberUuid, daysBack);
    },
    enabled: isAuthenticated && !!memberUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

export function useSaveConfigMutation() {
  const { showNotification } = useUIStore();

  return useMutation({
    mutationFn: (config: AppConfiguration) =>
      optimizationApi.saveConfiguration(config),
    onSuccess: () => {
      showNotification({
        type: 'success',
        message: 'Configuration saved successfully',
      });
    },
    onError: (error: unknown) => {
      debug.error('Save config failed:', error);
      const apiError = error as { response?: { data?: { detail?: string } } };
      showNotification({
        type: 'error',
        message: apiError.response?.data?.detail || 'Failed to save configuration',
      });
    },
  });
}

export function useColdStartMutation() {
  const { showNotification } = useUIStore();

  return useMutation({
    mutationFn: (params: ColdStartOptimizationRequest) =>
      optimizationApi.runColdstartOptimization(params),
    onSuccess: (result) => {
      debug.log('Cold start optimization completed:', result);
      const keywordCount = result.performance_analysis?.total_keywords || 0;
      showNotification({
        type: 'success',
        message: `Cold start optimization completed! ${keywordCount} keywords analyzed.`,
      });
    },
    onError: (error: unknown) => {
      debug.error('Cold start optimization failed:', error);
      const apiError = error as { response?: { data?: { detail?: string } } };
      showNotification({
        type: 'error',
        message: apiError.response?.data?.detail || 'Cold start optimization failed. Please try again.',
      });
    },
  });
}